import { tv } from 'tailwind-variants'
import { ContextualMenuAnchor, ContextualMenuPosition } from './types'

export const contexutalMenuClasses = tv({
  base: 'duration-100',
  variants: {
    position: {
      'top-left': 'bottom-full left-0 origin-bottom-left translate-y-1',
      'top-right': 'bottom-full right-0 origin-bottom-right translate-y-1',
      'top-center':
        'bottom-full left-1/2 -translate-x-1/2 origin-bottom-center translate-y-1',
      'bottom-left': 'top-full left-0 origin-top-left -translate-y-1',
      'bottom-right': 'top-full right-0 origin-top-right -translate-y-1',
      'bottom-center':
        'top-full left-1/2 -translate-x-1/2 origin-top-center -translate-y-1'
    }
  }
})

export const getPositioningString = (
  anchor: ContextualMenuAnchor
): ContextualMenuPosition => {
  return `${anchor.y}-${anchor.x}`
}

export const checkIfVectorAFitsWithinVectorB = (
  { x, x2 },
  { x: x3, x2: x4 }
): true | { x: boolean; x2: boolean } => {
  if (x < x3 && x2 > x4) {
    return { x: false, x2: false }
  }
  if (x < x3) {
    return { x: false, x2: true }
  }
  if (x2 > x4) {
    return { x: true, x2: false }
  }
  return true
}

const getXCenteredMenuVector = (button: DOMRect, menu: DOMRect) => {
  const { x: buttonX, width: buttonWidth } = button
  const { width: menuWidth } = menu
  const x = buttonX + buttonWidth / 2 - menuWidth / 2
  const x2 = x + menuWidth
  return { x, x2 }
}

export const getPositioningCoords = (
  { x: preferredX, y: preferredY }: ContextualMenuAnchor,
  buttonRect: DOMRect,
  menuRect: DOMRect
) => {
  const {
    innerHeight: viewportHeight,
    innerWidth: viewportWidth,
    scrollY: viewportTop
  } = window

  const updateMenuYPosition = (
    anchorY: ContextualMenuAnchor['y']
  ): ContextualMenuAnchor['y'] => {
    const { height: menuHeight } = menuRect
    const { y: buttonY, height: buttonHeight } = buttonRect

    let menuY: number, menuY2: number

    if (anchorY === 'top') {
      menuY = buttonY - menuHeight
      menuY2 = buttonY
    }
    if (anchorY === 'bottom') {
      menuY = buttonY + buttonHeight
      menuY2 = menuY + menuHeight
    }

    const defaultVector = checkIfVectorAFitsWithinVectorB(
      { x: menuY, x2: menuY2 },
      { x: viewportTop, x2: viewportHeight + viewportTop }
    )

    if (defaultVector === true) {
      return anchorY
    } else {
      return anchorY === 'top' ? 'bottom' : 'top'
    }
  }

  const updateMenuXPosition = (
    anchorX: ContextualMenuAnchor['x']
  ): ContextualMenuAnchor['x'] => {
    const { width: menuWidth } = menuRect
    const { x: buttonX, width: buttonWidth } = buttonRect
    const buttonX2 = buttonX + buttonWidth
    let menuX: number, menuX2: number

    if (anchorX === 'left') {
      menuX = buttonX
    }
    if (anchorX === 'right') {
      menuX = buttonX2 - menuWidth
    }
    if (anchorX === 'center') {
      menuX = getXCenteredMenuVector(buttonRect, menuRect).x
    }
    menuX2 = menuX + menuWidth

    const defaultVector = checkIfVectorAFitsWithinVectorB(
      { x: menuX, x2: menuX2 },
      { x: 0, x2: viewportWidth }
    )

    // figure out x
    if (defaultVector === true) {
      return anchorX
    } else if (anchorX !== 'center') {
      const centerVector = checkIfVectorAFitsWithinVectorB(
        getXCenteredMenuVector(buttonRect, menuRect),
        { x: 0, x2: viewportWidth }
      )

      if (centerVector === true) {
        return 'center'
      } else {
        if (centerVector.x === false && centerVector.x2 === false) {
          return 'left'
        }
        if (centerVector.x === false) {
          return 'left'
        }
        if (centerVector.x2 === false) {
          return 'right'
        }
      }
    }
  }

  return {
    x: updateMenuXPosition(preferredX),
    y: updateMenuYPosition(preferredY)
  }
}
